import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { fetchMyGroupBaskets } from "../api/groupBasketApi";
import { customerIdOf } from "./useAnalyticsUser";

/*
 * The shopper's own Order together baskets (GET /api/group-baskets/mine,
 * contract 5.3), for the "resume" cards on the storefront and the order
 * history. One request per signed-in customer, shared by every caller and
 * kept for a short while; a signed-out visitor never asks at all.
 */
const KEEP_MS = 30 * 1000;
const requests = new Map();

const ENDED = ["RELEASED", "CANCELLED"];

const basketsFor = (customerId) => {
  const kept = requests.get(customerId);
  if (kept && Date.now() - kept.at < KEEP_MS) return kept.promise;
  const promise = Promise.resolve()
    .then(() => fetchMyGroupBaskets())
    .then((answer) => ({ ...answer, receivedAt: Date.now() }))
    .catch(() => {
      requests.delete(customerId);
      return null;
    });
  requests.set(customerId, { promise, at: Date.now() });
  return promise;
};

/* After a create, a join or a leave: the next caller asks again. */
export const forgetMyGroupBaskets = () => requests.clear();

/* Baskets still under way: anything but released or cancelled. */
export const activeGroupBaskets = (baskets) =>
  (Array.isArray(baskets) ? baskets : []).filter((basket) => basket && !ENDED.includes(basket.state));

/* The active baskets at one restaurant, by the id its cart quotes with. */
export const groupBasketsAtVendor = (baskets, vendorId) =>
  activeGroupBaskets(baskets).filter(
    (basket) => Boolean(vendorId) && String(basket.vendorId || "") === String(vendorId)
  );

/**
 * @returns {{ baskets: Array, serverNow: string|null, receivedAt: number }}
 *   baskets  the active ones only
 */
export default function useMyGroupBaskets() {
  const customerId = useSelector((state) => customerIdOf(state.user?.currentUser));
  const [answer, setAnswer] = useState(null);

  useEffect(() => {
    if (!customerId) {
      setAnswer(null);
      return undefined;
    }
    let active = true;
    basketsFor(customerId).then((result) => {
      if (active) setAnswer(result);
    });
    return () => {
      active = false;
    };
  }, [customerId]);

  return {
    baskets: customerId ? activeGroupBaskets(answer?.baskets) : [],
    serverNow: answer?.serverNow || null,
    receivedAt: answer?.receivedAt || 0,
  };
}
